import { Capacitor, registerPlugin } from "@capacitor/core";
import { Browser } from "@capacitor/browser";
import { apiRequest } from "./apiClient";
import { getRuntimeVersionLabel } from "./runtimeAppInfo";

const USAGE_EVENT = "fp:appReviewUsageChanged";
const ELIGIBILITY_CACHE_PREFIX = "fp:appReviewEligibility:v2";
const IOS_REVIEW_URL = "https://apps.apple.com/app/id6782569972?action=write-review";
const PLAY_REVIEW_URL = "https://play.google.com/store/apps/details?id=com.sercanyavuz.aileyonetimi";
const NativeAppReview = registerPlugin("AppReview");
const requestedThisSession = new Set();

const platform = () => {
  try { return Capacitor.getPlatform(); } catch { return "web"; }
};

const localDateKey = (date = new Date()) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const cacheKey = (uid) => `${ELIGIBILITY_CACHE_PREFIX}:${String(uid || "").trim()}:${platform()}`;

function readEligibility(uid) {
  try {
    const parsed = JSON.parse(localStorage.getItem(cacheKey(uid)) || "null");
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch {
    return null;
  }
}

function writeEligibility(uid, eligibility, trigger = null) {
  if (!uid || !eligibility) return;
  try {
    localStorage.setItem(cacheKey(uid), JSON.stringify({ ...eligibility, cachedAt: Date.now() }));
    window.dispatchEvent(new CustomEvent(USAGE_EVENT, { detail: { eligibility, trigger } }));
  } catch {}
}

async function runtimeVersion() {
  try { return await getRuntimeVersionLabel(); } catch { return ""; }
}

async function sendEvent(uid, eventType, {
  trigger = null,
  moduleKey = null,
  itemId = null,
  idempotencyKey = null,
  metadata = {},
} = {}) {
  if (!uid) return null;
  const appVersion = await runtimeVersion();
  const data = await apiRequest("/engagement/app-review/event", {
    method: "POST",
    body: JSON.stringify({
      platform: platform(), eventType, trigger, moduleKey, appVersion,
      idempotencyKey: idempotencyKey || (itemId ? `${eventType}:${trigger || "event"}:${itemId}` : null),
      metadata,
    }),
  });
  if (data?.eligibility) writeEligibility(uid, data.eligibility, trigger);
  return data;
}

export function getAppReviewEligibility(uid) {
  return readEligibility(uid) || { eligible: false, loading: true };
}

export async function refreshAppReviewEligibility(uid) {
  if (!uid) return { eligible: false };
  const appVersion = await runtimeVersion();
  const query = new URLSearchParams({ platform: platform(), appVersion });
  const data = await apiRequest(`/engagement/app-review/eligibility?${query.toString()}`);
  const eligibility = data?.eligibility || { eligible: false };
  writeEligibility(uid, eligibility);
  return eligibility;
}

export function recordAppUsageDay(uid, date = new Date()) {
  if (!uid || !Capacitor.isNativePlatform()) return;
  const day = localDateKey(date);
  const sentKey = `fp:appReviewUsageSent:${uid}:${platform()}:${day}`;
  try {
    if (sessionStorage.getItem(sentKey)) return;
    sessionStorage.setItem(sentKey, "1");
  } catch {}
  void sendEvent(uid, "usage_day", { idempotencyKey: `usage:${platform()}:${day}` }).catch(() => {});
}

export function recordMeaningfulReviewAction(uid, { trigger, moduleKey, itemId, metadata } = {}) {
  if (!uid || !trigger || !Capacitor.isNativePlatform()) return Promise.resolve(null);
  return sendEvent(uid, "meaningful_action", { trigger, moduleKey, itemId, metadata });
}

// Eski çağıranlar için geriye uyumluluk. Sayfa ziyareti ve ilk veri ekleme artık
// değerlendirme puanı üretmez; yalnız doğrulanmış değer anları kaydedilir.
export function recordReviewMilestone() {}

export async function requestNativeAppReview(uid, trigger = "safe_moment") {
  if (!uid || !Capacitor.isNativePlatform()) return { status: "unavailable", platform: platform() };
  const sessionKey = `${uid}:${platform()}`;
  if (requestedThisSession.has(sessionKey)) return { status: "already_attempted", platform: platform() };
  requestedThisSession.add(sessionKey);
  await sendEvent(uid, "native_request_started", {
    trigger,
    idempotencyKey: `request:${platform()}:${Date.now()}`,
  }).catch(() => null);
  try {
    if (platform() === "android") {
      const result = await NativeAppReview.requestReview();
      const eventType = result?.status === "completed" ? "native_flow_completed" : "native_request_accepted";
      await sendEvent(uid, eventType, { trigger, idempotencyKey: `${eventType}:${platform()}:${Date.now()}` });
      return { status: result?.status || "accepted", platform: "android" };
    }
    const handler = window.webkit?.messageHandlers?.aileNativeBridge;
    if (!handler) {
      await sendEvent(uid, "native_unavailable", { trigger, idempotencyKey: `unavailable:ios:${Date.now()}` });
      return { status: "unavailable", platform: "ios" };
    }
    handler.postMessage({ action: "requestAppReview" });
    await sendEvent(uid, "native_request_accepted", { trigger, idempotencyKey: `accepted:ios:${Date.now()}` });
    return { status: "accepted", platform: "ios" };
  } catch (error) {
    await sendEvent(uid, "native_error", {
      trigger,
      idempotencyKey: `error:${platform()}:${Date.now()}`,
      metadata: { code: String(error?.code || "native_error").slice(0, 80) },
    }).catch(() => {});
    return { status: "error", platform: platform(), error };
  }
}

export async function openStoreReviewPage(uid) {
  if (!Capacitor.isNativePlatform()) return { status: "unavailable" };
  const currentPlatform = platform();
  try {
    if (currentPlatform === "android") {
      await NativeAppReview.openStoreListing();
    } else {
      const handler = window.webkit?.messageHandlers?.aileNativeBridge;
      if (handler) handler.postMessage({ action: "openAppReviewPage" });
      else await Browser.open({ url: IOS_REVIEW_URL, presentationStyle: "popover" });
    }
    await sendEvent(uid, "store_listing_opened", {
      trigger: "settings_about",
      idempotencyKey: `store-open:${currentPlatform}:${Date.now()}`,
    }).catch(() => {});
    return { status: "opened" };
  } catch (error) {
    if (currentPlatform === "android") {
      try {
        await Browser.open({ url: PLAY_REVIEW_URL });
        await sendEvent(uid, "store_listing_opened", { trigger: "settings_about_https_fallback" }).catch(() => {});
        return { status: "opened" };
      } catch {}
    }
    return { status: "error", error };
  }
}

export function subscribeAppReviewUsage(listener) {
  if (typeof listener !== "function") return () => {};
  window.addEventListener(USAGE_EVENT, listener);
  return () => window.removeEventListener(USAGE_EVENT, listener);
}

export const REVIEW_MILESTONE_BY_MODULE = Object.freeze({});
